/**
 * Admin v2 TanStack Query hooks.
 *
 * v1 reference: ``ando-frontend/app/pages/HypermemAdminPage.tsx`` +
 * ``OrganizationsPage.tsx`` (3403 LoC monolith). The v2 rewrite is
 * **AUTHORIZED** per memory rule "v2 rewrite authorized for monoliths
 * >1500 LoC" — we ship a fresh implementation that:
 *   - reads BE-side aggregates ONLY (privacy-by-default, see admin v2
 *     SOTA brief from May 2026),
 *   - exposes per-section hooks so the dashboard renders incrementally,
 *   - centralises Inspect Mode (the 15-min PII unlock).
 *
 * Polling: dashboards are not real-time. We use TanStack Query's stale
 * times to control freshness without thrashing the BE.
 */

import { useQuery } from "@tanstack/react-query";

import {
  getAdminAutomation,
  getAdminCanvas,
  getAdminCost,
  getAdminCostReconciliation,
  getAdminCostTraceDetail,
  getAdminEngagement,
  getAdminHealth,
  getAdminOverview,
  getAdminQuality,
  getAdminRevenue,
  getAdminUser,
  getAdminUserTimeline,
  getInspectModeStatus,
  getZitadelAccountState,
} from "@ando/core";

interface RangeArgs {
  start?: string;
  end?: string;
}

export const adminQueryKeys = {
  root: ["admin"] as const,
  overview: (args: RangeArgs) =>
    ["admin", "overview", args.start ?? null, args.end ?? null] as const,
  engagement: (args: RangeArgs) =>
    ["admin", "engagement", args.start ?? null, args.end ?? null] as const,
  cost: (args: RangeArgs) =>
    ["admin", "cost", args.start ?? null, args.end ?? null] as const,
  costTrace: (traceId: string) => ["admin", "cost", "trace", traceId] as const,
  costReconciliation: (args: RangeArgs) =>
    ["admin", "cost", "reconciliation", args.start ?? null, args.end ?? null] as const,
  quality: (args: RangeArgs) =>
    ["admin", "quality", args.start ?? null, args.end ?? null] as const,
  health: (args: RangeArgs) =>
    ["admin", "health", args.start ?? null, args.end ?? null] as const,
  automation: (args: RangeArgs) =>
    ["admin", "automation", args.start ?? null, args.end ?? null] as const,
  canvas: (args: RangeArgs) =>
    ["admin", "canvas", args.start ?? null, args.end ?? null] as const,
  revenue: () => ["admin", "revenue"] as const,
  user: (userId: string) => ["admin", "user", userId] as const,
  userTimeline: (userId: string, args: RangeArgs) =>
    [
      "admin",
      "user-timeline",
      userId,
      args.start ?? null,
      args.end ?? null,
    ] as const,
  inspect: (tenantId: string, scopeKey: string) =>
    ["admin", "inspect", tenantId, scopeKey] as const,
  zitadelState: (userId: string) => ["admin", "zitadel", userId] as const,
};

export function useAdminOverview(args: RangeArgs = {}) {
  return useQuery({
    queryKey: adminQueryKeys.overview(args),
    queryFn: () => getAdminOverview(args),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useAdminEngagement(args: RangeArgs = {}) {
  return useQuery({
    queryKey: adminQueryKeys.engagement(args),
    queryFn: () => getAdminEngagement(args),
    staleTime: 5 * 60_000,
  });
}

export function useAdminCost(args: RangeArgs = {}) {
  return useQuery({
    queryKey: adminQueryKeys.cost(args),
    queryFn: () => getAdminCost(args),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/**
 * Recorded-vs-Google reconciliation (project-level). The Cloud Monitoring
 * round-trip is slower + rate-limited, so a longer stale time than the other
 * cost hooks. Never errors hard — the BE returns ``available: false`` when
 * Monitoring/ADC is unreachable.
 */
export function useAdminCostReconciliation(args: RangeArgs = {}) {
  return useQuery({
    queryKey: adminQueryKeys.costReconciliation(args),
    queryFn: () => getAdminCostReconciliation(args),
    staleTime: 5 * 60_000,
  });
}

export function useAdminCostTrace(traceId: string | null) {
  return useQuery({
    queryKey: adminQueryKeys.costTrace(traceId ?? "__none__"),
    queryFn: () => getAdminCostTraceDetail(traceId as string),
    enabled: traceId !== null,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAdminQuality(args: RangeArgs = {}) {
  return useQuery({
    queryKey: adminQueryKeys.quality(args),
    queryFn: () => getAdminQuality(args),
    staleTime: 5 * 60_000,
  });
}

export function useAdminHealth(args: RangeArgs = {}) {
  return useQuery({
    queryKey: adminQueryKeys.health(args),
    queryFn: () => getAdminHealth(args),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useAdminCanvas(args: RangeArgs = {}) {
  return useQuery({
    queryKey: adminQueryKeys.canvas(args),
    queryFn: () => getAdminCanvas(args),
    staleTime: 5 * 60_000,
  });
}

export function useAdminRevenue() {
  return useQuery({
    queryKey: adminQueryKeys.revenue(),
    queryFn: () => getAdminRevenue(),
    staleTime: 5 * 60_000,
  });
}

export function useAdminAutomationAnalytics(args: RangeArgs = {}) {
  return useQuery({
    queryKey: adminQueryKeys.automation(args),
    queryFn: () => getAdminAutomation(args),
    staleTime: 60_000,
  });
}

/** Single admin user (``GET /admin/users/{id}``) — powers the detail shell. */
export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: adminQueryKeys.user(userId ?? "__none__"),
    queryFn: () => getAdminUser(userId as string),
    enabled: userId !== null,
    staleTime: 30_000,
  });
}

export function useAdminUserTimeline(args: {
  userId: string | null;
  tenantId?: string;
  start?: string;
  end?: string;
}) {
  return useQuery({
    queryKey: adminQueryKeys.userTimeline(args.userId ?? "__none__", {
      start: args.start,
      end: args.end,
    }),
    queryFn: () =>
      getAdminUserTimeline({
        userId: args.userId as string,
        tenantId: args.tenantId,
        start: args.start,
        end: args.end,
      }),
    enabled: args.userId !== null,
    staleTime: 60_000,
  });
}

export function useInspectModeStatus(args: {
  tenantId: string | null;
  scopeKey: string;
}) {
  return useQuery({
    queryKey: adminQueryKeys.inspect(args.tenantId ?? "__none__", args.scopeKey),
    queryFn: () =>
      getInspectModeStatus({
        tenant_id: args.tenantId as string,
        scope_key: args.scopeKey,
      }),
    enabled: args.tenantId !== null,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useZitadelAccountState(userId: string | null) {
  return useQuery({
    queryKey: adminQueryKeys.zitadelState(userId ?? "__none__"),
    queryFn: () => getZitadelAccountState(userId as string),
    enabled: userId !== null,
    staleTime: 30_000,
  });
}
